import { TRACKER_IDS, TRACKER_NAMES, type CasinoRow, type Payload, type TrackerId, type WindowKey } from '../api/types';
import { median, pct, spreadOf, usd } from './format';

/* Reports, written from the data.

   Every story is generated from what the trackers published in the current
   collection, so it is always current and never invents a figure. The prose
   says what each tracker reported, never what a casino "really" did: the site
   does not know which tracker is right, and the stories do not pretend to.

   `**text**` in a paragraph renders as bold. */

export type StoryKind = 'market' | 'gap' | 'balance' | 'unseen';

export const KIND_LABEL: Record<StoryKind, string> = {
  market: 'Market',
  gap: 'Disagreements',
  balance: 'Hot wallets',
  unseen: 'Coverage',
};

export const W_LABEL: Record<WindowKey, string> = {
  '1d': '24 hours', '7d': '7 days', '14d': '14 days', '30d': '30 days', '90d': '90 days', '365d': 'year',
};
const W_ORDER: WindowKey[] = ['1d', '7d', '14d', '30d', '90d', '365d'];

export interface StoryReading { id: TrackerId; value: number; counted: boolean; wallets?: number | null }

export interface Story {
  id: string;
  kind: StoryKind;
  kicker: string;
  title: string;
  dek: string;
  casinoKey?: string;
  casinoName?: string;
  figure: string;
  figureLabel: string;
  readings: StoryReading[];
  stats: { value: string; label: string }[];
  paragraphs: string[];
  pull?: string;
  windows?: { w: WindowKey; value: number }[];
  minutes: number;
  /** Cover art, assigned from the story's kind pool once every story is built. */
  cover?: string;
}

/* Cover art per kind, generated with Higgsfield (design/higgsfield-assets.md).
   Stories of one kind take the pool in order, so no two stories on the page
   share a cover while the pool lasts; the original cover of each kind comes
   last. */
export const COVERS: Record<StoryKind, string[]> = {
  gap: ['/img/covers/gap-1.webp', '/img/covers/gap-2.webp', '/img/covers/gap-3.webp', '/img/covers/gap-4.webp', '/img/covers/gap-5.webp', '/img/cover-gap.webp'],
  balance: ['/img/covers/balance-1.webp', '/img/covers/balance-2.webp', '/img/covers/balance-3.webp', '/img/cover-balance.webp'],
  unseen: ['/img/covers/unseen-1.webp', '/img/covers/unseen-2.webp', '/img/covers/unseen-3.webp', '/img/cover-unseen.webp'],
  market: ['/img/covers/market-1.webp', '/img/cover-market.webp'],
};

const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const names = (ids: TrackerId[]) => ids.map((id) => TRACKER_NAMES[id]);
function list(items: string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}
const ends = (rs: StoryReading[]) => {
  const counted = rs.filter((r) => r.counted);
  const pool = counted.length >= 2 ? counted : rs;
  const sorted = [...pool].sort((a, b) => a.value - b.value);
  return { lo: sorted[0] as StoryReading, hi: sorted[sorted.length - 1] as StoryReading, pool };
};
const minutes = (paras: string[]) => Math.max(1, Math.round(paras.join(' ').split(/\s+/).length / 200));

const WALLETS_CAVEAT = 'Trackers see a casino through the deposit and hot-wallet addresses they have managed to identify. A tracker that has found fewer of them will report less money without being wrong about the wallets it does watch, which is why HouseGap shows the spread instead of picking a winner.';

function readingsOf(row: CasinoRow): StoryReading[] {
  return TRACKER_IDS.flatMap((id) => {
    const v = row.by[id]?.value;
    return finite(v) ? [{ id, value: v, counted: row.consensusBy.includes(id) }] : [];
  });
}

function windowsOf(data: Payload, key: string) {
  return W_ORDER.flatMap((w) => {
    const r = data.analysis.volume[w]?.rows.find((x) => x.key === key);
    return r && r.consensusBy.length > 1 && finite(r.spreadPct) ? [{ w, value: r.spreadPct }] : [];
  });
}

export function buildStories(data: Payload, win: WindowKey): Story[] {
  const a = data.analysis.volume[win];
  if (!a) return [];
  const W = W_LABEL[win];
  const out: Story[] = [];

  /* ---------------------------------------------------------- the market */
  const market: StoryReading[] = a.consensusSources.flatMap((id) => {
    const v = a.commonTotals?.[id];
    return finite(v) ? [{ id, value: v, counted: true }] : [];
  });
  const mgap = spreadOf(market.map((r) => r.value));
  if (market.length >= 2 && finite(mgap)) {
    const { lo, hi } = ends(market);
    const mid = median(market.map((r) => r.value));
    const paragraphs = [
      `Over the last ${W}, ${list(market.map((r) => `**${TRACKER_NAMES[r.id]}** reports ${usd(r.value)}`))} in deposits, all measured over the same **${a.commonCount ?? 0} casinos**. The median of those figures is ${usd(mid)}.`,
      `That leaves the highest and lowest trackers **${pct(mgap)} apart** on the size of one market. Because every figure covers the same casino list, the gap cannot be explained by one tracker simply following more casinos.`,
      WALLETS_CAVEAT,
      `Across every casino any tracker lists, ${a.rows.length} in this window, only ${a.rows.filter((r) => r.consensusBy.length >= 2).length} are published by two or more counted trackers. The rest can be read but not cross-checked.`,
    ];
    out.push({
      id: 'market', kind: 'market', kicker: 'Market · The shared list',
      title: `The trackers are ${pct(mgap, 0)} apart on the size of the whole market`,
      dek: `Same ${a.commonCount ?? 0} casinos, same ${W}. ${TRACKER_NAMES[hi.id]} counts ${usd(hi.value)} of deposits and ${TRACKER_NAMES[lo.id]} counts ${usd(lo.value)}.`,
      figure: pct(mgap, 0), figureLabel: 'apart',
      readings: market,
      stats: [
        { value: pct(mgap, 0), label: 'highest to lowest' },
        { value: usd(mid), label: 'median deposits' },
        { value: String(a.commonCount ?? 0), label: 'casinos on every tracker' },
        { value: String(market.length), label: 'trackers counted' },
      ],
      paragraphs,
      pull: `One market, one ${W}: ${usd(hi.value)} or ${usd(lo.value)}.`,
      minutes: minutes(paragraphs),
    });
  }

  /* --------------------------------------------- per-casino disagreements */
  const rows = a.rows.filter((r) => r.consensusBy.length > 1 && finite(r.spreadPct));
  const big = rows.filter((r) => (r.consensus ?? 0) >= 1_000_000);
  const gaps = (big.length >= 3 ? big : rows).sort((x, y) => (y.spreadPct ?? 0) - (x.spreadPct ?? 0)).slice(0, 5);
  gaps.forEach((row, i) => {
    const readings = readingsOf(row);
    const { lo, hi, pool } = ends(readings);
    const windows = windowsOf(data, row.key);
    const held = windows.filter((w) => w.value >= 15).length;
    const spread = row.spreadPct as number;
    const refs = readings.filter((r) => !r.counted);

    const titles = [
      `${row.name}: the trackers are ${pct(spread, 0)} apart`,
      `How big is ${row.name}? It depends which tracker you ask`,
      `${usd(hi.value)} or ${usd(lo.value)}: two readings of ${row.name}`,
    ];
    const paragraphs = [
      `Over the last ${W}, ${list(pool.map((r) => `**${TRACKER_NAMES[r.id]}** reports ${usd(r.value)}`))} in deposits at **${row.name}**. The median of the counted figures is ${usd(row.consensus)}, and the spread from highest to lowest is **${pct(spread)}**.`,
      windows.length >= 2
        ? `This is not one noisy week. Measured over ${list(windows.map((w) => `${W_LABEL[w.w]} (${pct(w.value, 0)})`))}, the trackers stay apart${held >= 3 ? ', and the gap is above 15% in ' + held + ' of those windows' : ''}.`
        : `This is the only window in which more than one tracker publishes a figure for ${row.name}, so there is not yet enough to say whether the gap persists.`,
      WALLETS_CAVEAT,
      ...(refs.length ? [`${list(names(refs.map((r) => r.id)))} also publish${refs.length === 1 ? 'es' : ''} a figure (${list(refs.map((r) => usd(r.value)))}), but ${refs.length === 1 ? 'it is' : 'they are'} reference-only and not counted toward the median.`] : []),
    ];
    out.push({
      id: `gap-${row.key}`, kind: 'gap', casinoKey: row.key, casinoName: row.name,
      kicker: `Disagreement · ${row.name}`,
      title: titles[i % titles.length] as string,
      dek: `${TRACKER_NAMES[hi.id]} puts deposits over the last ${W} at ${usd(hi.value)}. ${TRACKER_NAMES[lo.id]} puts them at ${usd(lo.value)}.`,
      figure: pct(spread, 0), figureLabel: 'apart',
      readings, windows,
      stats: [
        { value: pct(spread, 0), label: 'highest to lowest' },
        { value: usd(row.consensus), label: 'median deposits' },
        { value: usd(hi.value), label: `${TRACKER_NAMES[hi.id]}, highest` },
        { value: usd(lo.value), label: `${TRACKER_NAMES[lo.id]}, lowest` },
      ],
      paragraphs,
      pull: `Same casino, same ${W}: ${usd(hi.value)} or ${usd(lo.value)}.`,
      minutes: minutes(paragraphs),
    });
  });

  /* ---------------------------------------------------------- hot wallets */
  const bal = data.analysis.balances;
  if (bal) {
    const bRows = bal.rows
      .filter((r) => r.reportedBy.length >= 2 && finite(r.spreadPct) && (r.consensus ?? 0) >= 1_000_000)
      .sort((x, y) => (y.spreadPct ?? 0) - (x.spreadPct ?? 0))
      .slice(0, 3);
    for (const row of bRows) {
      const readings: StoryReading[] = TRACKER_IDS.flatMap((id) => {
        const b = row.by[id];
        return b && finite(b.value) ? [{ id, value: b.value, counted: true, wallets: b.wallets ?? null }] : [];
      });
      if (readings.length < 2) continue;
      const { lo, hi } = ends(readings);
      const withCounts = readings.filter((r) => finite(r.wallets));
      const paragraphs = [
        `Right now, ${list(readings.map((r) => `**${TRACKER_NAMES[r.id]}** reports ${usd(r.value)}`))} sitting in **${row.name}**'s hot wallets.`,
        withCounts.length
          ? `The wallet counts explain part of it: ${list(withCounts.map((r) => `${TRACKER_NAMES[r.id]} watches ${r.wallets} wallet${r.wallets === 1 ? '' : 's'}`))}. A tracker that has found more addresses will see more money in them.`
          : 'None of the trackers publishes how many wallets it watches for this casino, so the gap cannot be traced to a difference in coverage from the published data alone.',
        'Hot-wallet balances are a snapshot, not a flow: they move every time the casino tops up or sweeps a wallet, so part of any gap can simply be timing between the trackers’ own collections.',
      ];
      out.push({
        id: `balance-${row.key}`, kind: 'balance', casinoKey: row.key, casinoName: row.name,
        kicker: `Hot wallets · ${row.name}`,
        title: `${row.name}'s hot wallets: ${usd(hi.value)}, or ${usd(lo.value)}, depending on who is counting`,
        dek: `The trackers are ${pct(row.spreadPct, 0)} apart on how much money ${row.name} is holding right now.`,
        figure: pct(row.spreadPct, 0), figureLabel: 'apart',
        readings,
        stats: [
          { value: pct(row.spreadPct, 0), label: 'highest to lowest' },
          { value: usd(row.consensus), label: 'median balance' },
          { value: usd(hi.value), label: `${TRACKER_NAMES[hi.id]}, highest` },
          { value: usd(lo.value), label: `${TRACKER_NAMES[lo.id]}, lowest` },
        ],
        paragraphs,
        pull: `One casino's wallets, read three ways.`,
        minutes: minutes(paragraphs),
      });
    }
  }

  /* ------------------------------------------------ seen by one tracker */
  const lone = a.rows
    .filter((r) => r.reportedBy.length === 1)
    .map((r) => ({ row: r, id: r.reportedBy[0] as TrackerId, value: r.by[r.reportedBy[0] as TrackerId]?.value }))
    .filter((x): x is { row: CasinoRow; id: TrackerId; value: number } => finite(x.value) && x.value >= 1_000_000)
    .sort((x, y) => y.value - x.value)
    .slice(0, 3);
  for (const { row, id, value } of lone) {
    const others = TRACKER_IDS.filter((t) => t !== id);
    const paragraphs = [
      `**${TRACKER_NAMES[id]}** reports ${usd(value)} in deposits at **${row.name}** over the last ${W}. ${list(names(others))} publish no figure for it at all in this window.`,
      '**Missing is not zero.** A tracker that does not list a casino has not measured it as empty; it simply has not identified its wallets, or does not follow it. So this figure stands alone: it cannot be confirmed or contradicted from the other trackers.',
      'That is why a casino like this is left out of every cross-tracker comparison on HouseGap until a second tracker picks it up.',
    ];
    out.push({
      id: `unseen-${row.key}`, kind: 'unseen', casinoKey: row.key, casinoName: row.name,
      kicker: `Coverage · ${row.name}`,
      title: `${row.name} took ${usd(value)} in ${W}, and only ${TRACKER_NAMES[id]} lists it`,
      dek: `Every other tracker is silent on ${row.name}. That is a gap in coverage, not a casino with no deposits.`,
      figure: usd(value), figureLabel: `${TRACKER_NAMES[id]} only`,
      readings: [{ id, value, counted: true }],
      stats: [
        { value: usd(value), label: `${TRACKER_NAMES[id]}, ${W}` },
        { value: '1', label: 'tracker lists it' },
        { value: String(others.length), label: 'trackers silent' },
        { value: '—', label: 'cross-check possible' },
      ],
      paragraphs,
      minutes: minutes(paragraphs),
    });
  }

  const used: Partial<Record<StoryKind, number>> = {};
  for (const story of out) {
    const pool = COVERS[story.kind];
    const n = used[story.kind] ?? 0;
    const art = pool[n % pool.length];
    if (art) story.cover = art;
    used[story.kind] = n + 1;
  }
  return out;
}
